import type { JSX } from 'react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, Input } from '@artosera/ui';
import type { RefSource } from '../../../../lib/correspondance.js';
import styles from './Correspondance.module.css';

/**
 * Une référence à un champ de la source : un en-tête ou un chemin (`artiste.nom`,
 * `images[0].url`), dans la source principale, dans une source jointe (rôle),
 * ou dans l'enregistrement dont on a déplié un tableau. Les colonnes que le
 * profil connaît se proposent en suggestions ; un chemin libre reste permis.
 */
export function RefEditeur({
  valeur,
  onChange,
  libelle,
  suggestions,
  roles,
  avecParent,
  invalide = false,
  decrit,
}: {
  valeur: RefSource;
  onChange: (suite: RefSource) => void;
  libelle: string;
  suggestions: readonly string[];
  roles: readonly string[];
  /** Un tableau est déplié : « parent » désigne l'enregistrement d'origine. */
  avecParent: boolean;
  invalide?: boolean;
  decrit?: string;
}): JSX.Element {
  const { t } = useTranslation('console');
  const identifiant = useId();
  return (
    <div className={styles.reference}>
      <div className={styles.referenceChamp}>
        <label htmlFor={`${identifiant}-champ`}>{libelle}</label>
        <Input
          id={`${identifiant}-champ`}
          list={`${identifiant}-liste`}
          value={valeur.champ}
          maxLength={300}
          aria-invalid={invalide || undefined}
          aria-describedby={decrit}
          autoComplete="off"
          onChange={(evenement) => onChange({ ...valeur, champ: evenement.target.value })}
        />
        <datalist id={`${identifiant}-liste`}>
          {suggestions.map((suggestion) => (
            <option key={suggestion} value={suggestion} />
          ))}
        </datalist>
      </div>
      {roles.length > 1 ? (
        <div className={styles.referenceRole}>
          <label htmlFor={`${identifiant}-role`}>{t('correspondance.editeur.role')}</label>
          <select
            id={`${identifiant}-role`}
            className={styles.selection}
            value={valeur.role ?? ''}
            onChange={(evenement) =>
              onChange({
                ...valeur,
                role: evenement.target.value === '' ? null : evenement.target.value,
              })
            }
          >
            <option value="">{t('correspondance.editeur.rolePrincipal')}</option>
            {roles.slice(1).map((role) => (
              <option key={role} value={role}>
                {role}
              </option>
            ))}
          </select>
        </div>
      ) : null}
      {avecParent ? (
        <Checkbox
          id={`${identifiant}-parent`}
          checked={valeur.parent}
          onCheckedChange={(coche) => onChange({ ...valeur, parent: coche === true })}
          label={t('correspondance.editeur.parent')}
        />
      ) : null}
    </div>
  );
}
